import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, Signal, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { formatMultiplier, getOptionType, isDiscountOptionType, OptionType } from '../../enums/OptionType';
import { OnlineOrderType } from '../../models/OnlineOrder.model';
import OptionGroup from '../../models/OptionGroup.model';
import Product from '../../models/Product.model';
import ProductOption from '../../models/ProductOption.model';
import { AppSettingsService } from '../../services/app-settings.service';
import { CartService } from '../../services/cart.service';
import { MenuService } from '../../services/menu.service';
import { ModalService } from '../../services/modal.service';
import { FormatPricePipe } from '../../utility/pipes/format-price.pipe';
import { TextUtils } from '../../utility/text.utils';

export interface GroupedOptions {
  group: OptionGroup;
  options: ProductOption[];
}

@Component({
  selector: 'app-options-modal',
  standalone: true,
  imports: [CommonModule, TranslateModule, FormsModule, FormatPricePipe],
  templateUrl: './options-modal.html',
})
export class OptionsModalComponent {
  public modalService = inject(ModalService);
  public cartService = inject(CartService);
  private settingsService = inject(AppSettingsService);
  private menuService = inject(MenuService);

  OptionType = OptionType;

  activeMenuLang = this.settingsService.activeMenuLanguage;
  currency = this.settingsService.selectedCurrency;

  public quantity = signal<number>(1);
  public selectedOptions = signal<Map<string, ProductOption>>(new Map());
  public notes = signal<string>('');

  get companyCurrency(): string {
    return this.currency()?.Symbol || '$';
  }

  get currencyPrecision(): number {
    const p = this.currency()?.Precision;
    return typeof p === 'number' && !isNaN(p) ? p : 2;
  }

  product: Signal<Product | null> = this.modalService.product;
  allOptionGroups: Signal<OptionGroup[]> = this.menuService.optionGroups;

  isSellable = computed(() => {
    const p = this.product();
    return p ? p.IsSellable !== false : true;
  });

  // Effective base price based on cart order type (Price1 for dine-in, Price3 for pickup)
  productEffectiveBasePrice = computed(() => {
    const p = this.product();
    if (!p) return 0;
    const isPickup = this.cartService.orderType() === OnlineOrderType.PICKUP;
    if (isPickup) {
      return (typeof p.Price3 === 'number' && p.Price3 > 0)
        ? p.Price3
        : (typeof p.Price1 === 'number' ? p.Price1 : 0);
    }
    return typeof p.Price1 === 'number' ? p.Price1 : 0;
  });

  productEffectiveTaxPercent = computed(() => {
    const p = this.product();
    if (!p) return 0;
    const isPickup = this.cartService.orderType() === OnlineOrderType.PICKUP;
    if (isPickup) {
      return (typeof p.Tax3 === 'number' && p.Tax3 > 0)
        ? p.Tax3
        : (typeof p.Tax1 === 'number' ? p.Tax1 : 0);
    }
    return typeof p.Tax1 === 'number' ? p.Tax1 : 0;
  });

  /**
   * Resolves the effective OptionType for an option, falling back to group.Type if option.Type is undefined or 0.
   */
  resolveType(option: ProductOption, group?: OptionGroup): OptionType {
    const optType = option.Type !== undefined && option.Type !== null ? Number(option.Type) : undefined;
    if (optType !== undefined && optType !== 0) {
      return getOptionType(optType);
    }
    const resolvedGroup = group || (option.OptionGroupID ? this.allOptionGroups().find(g => g.id === option.OptionGroupID) : undefined);
    if (resolvedGroup?.Type !== undefined && resolvedGroup?.Type !== null) {
      return getOptionType(Number(resolvedGroup.Type));
    }
    return optType !== undefined ? getOptionType(optType) : OptionType.ADD;
  }

  getOptionKey(option: ProductOption | string): string {
    if (typeof option === 'string') return option;
    return option.id || option.OptionID || option.SID || '';
  }

  getOptionQuantity(option: ProductOption | string): number {
    const key = this.getOptionKey(option);
    return this.selectedOptions().get(key)?.Quantity || 0;
  }

  isOptionSelected(option: ProductOption | string): boolean {
    return this.getOptionQuantity(option) > 0;
  }

  /**
   * Groups options for this product, excluding any POS-only discount option types.
   */
  groupedOptions = computed(() => {
    const p = this.product();
    const allGroups = this.allOptionGroups();

    if (!p || !p.ProductOptions || allGroups.length === 0) {
      return [];
    }

    const optionsByGroupId = new Map<string, ProductOption[]>();
    for (const option of p.ProductOptions) {
      if (!optionsByGroupId.has(option.OptionGroupID)) {
        optionsByGroupId.set(option.OptionGroupID, []);
      }
      optionsByGroupId.get(option.OptionGroupID)!.push(option);
    }

    const result: GroupedOptions[] = [];
    for (const group of allGroups) {
      const options = optionsByGroupId.get(group.id);
      if (options && options.length > 0) {
        // Hide discount option types from QR menu (they are meant for POS only)
        const visibleOptions = options.filter(o => !isDiscountOptionType(this.resolveType(o, group)));
        if (visibleOptions.length > 0) {
          result.push({ group, options: visibleOptions });
        }
      }
    }

    return result;
  });

  /**
   * Real-time dynamic price calculation factoring in:
   * 1. Multiplier (scales base price)
   * 2. Additions (ADD and PRODUCT modifiers * quantity)
   * 3. Tax application
   */
  calculation = computed(() => {
    const basePrice = this.productEffectiveBasePrice();
    const selected = Array.from(this.selectedOptions().values());

    // 1. Multiplier
    let multiplier = 1.0;
    for (const opt of selected) {
      if (this.resolveType(opt) === OptionType.MULTIPLY && opt.Modifier > 0) {
        multiplier = opt.Modifier;
        break;
      }
    }
    const modifiedBasePrice = basePrice * multiplier;

    // 2. Additions (ADD and PRODUCT)
    let totalAdditions = 0;
    for (const opt of selected) {
      const type = this.resolveType(opt);
      if (type === OptionType.ADD || type === OptionType.PRODUCT) {
        const qty = opt.Quantity || 1;
        totalAdditions += (opt.Modifier || 0) * qty;
      }
    }
    const grossPrice = modifiedBasePrice + totalAdditions;

    // 3. Tax and total
    const taxPercent = this.productEffectiveTaxPercent();
    const grossUnitWithTax = grossPrice * (1 + taxPercent / 100);
    const totalPayable = Number((grossUnitWithTax * this.quantity()).toFixed(2));

    return {
      basePrice,
      multiplier,
      modifiedBasePrice,
      totalAdditions,
      grossPrice,
      totalPayable,
    };
  });

  totalPriceWithTax = computed(() => this.calculation().totalPayable);

  isSelectionValid = computed(() => {
    const groups = this.groupedOptions();
    const selected = Array.from(this.selectedOptions().values());

    for (const { group, options } of groups) {
      const min = this.getGroupMinQuantity(group, options);
      const max = this.getGroupMaxQuantity(group, options);
      const countInGroup = selected
        .filter(o => o.OptionGroupID === group.id)
        .reduce((sum, o) => sum + (o.Quantity || 1), 0);

      if (min > 0 && countInGroup < min) {
        return false;
      }
      if (max > 0 && countInGroup > max) {
        return false;
      }
    }
    return true;
  });

  constructor() {
    effect(() => {
      // Whenever modal opens with a product, reset and auto-preselect standard choices
      if (this.modalService.isOpen()) {
        untracked(() => {
          this.initSelections();
        });
      }
    });
  }

  /**
   * Initializes selections with mandatory defaults:
   * - MULTIPLY: preselects 1.0x if available, otherwise first option.
   * - SIZE: preselects first option.
   */
  private initSelections() {
    const newSelected = new Map<string, ProductOption>();
    const groups = this.groupedOptions();

    for (const { group, options } of groups) {
      const hasMultiply = options.some(o => this.resolveType(o, group) === OptionType.MULTIPLY);
      const hasSize = options.some(o => this.resolveType(o, group) === OptionType.SIZE);

      if (hasMultiply) {
        const defaultOpt = options.find(o => o.Modifier === 1.0) || options[0];
        if (defaultOpt) {
          const key = this.getOptionKey(defaultOpt);
          const type = this.resolveType(defaultOpt, group);
          newSelected.set(key, { ...defaultOpt, Type: type, Quantity: 1 });
        }
      } else if (hasSize && options.length > 0) {
        const key = this.getOptionKey(options[0]);
        const type = this.resolveType(options[0], group);
        newSelected.set(key, { ...options[0], Type: type, Quantity: 1 });
      }
    }

    this.selectedOptions.set(newSelected);
    this.quantity.set(1);
    this.notes.set('');
  }

  getGroupMinQuantity(group: OptionGroup, options: ProductOption[]): number {
    if (options.some(o => this.resolveType(o, group) === OptionType.MULTIPLY || this.resolveType(o, group) === OptionType.SIZE)) {
      return 1;
    }
    if (options.some(o => this.resolveType(o, group) === OptionType.REMOVABLE || isDiscountOptionType(this.resolveType(o, group)))) {
      return 0;
    }
    return group.MinQuantity || 0;
  }

  getGroupMaxQuantity(group: OptionGroup, options: ProductOption[]): number {
    if (options.some(o => this.resolveType(o, group) === OptionType.MULTIPLY || this.resolveType(o, group) === OptionType.SIZE)) {
      return 1;
    }
    if (options.some(o => this.resolveType(o, group) === OptionType.REMOVABLE)) {
      return 0; // 0 = unlimited
    }
    return group.MaxQuantity || 0;
  }

  isRadioGroup(group: OptionGroup, options: ProductOption[]): boolean {
    if (options.some(o => this.resolveType(o, group) === OptionType.MULTIPLY || this.resolveType(o, group) === OptionType.SIZE)) {
      return true;
    }
    return this.getGroupMaxQuantity(group, options) === 1 && this.getGroupMinQuantity(group, options) === 1;
  }

  isStepperGroup(group: OptionGroup, options: ProductOption[]): boolean {
    const isAddOrProduct = options.every(o => {
      const t = this.resolveType(o, group);
      return t === OptionType.ADD || t === OptionType.PRODUCT;
    });
    return isAddOrProduct && this.getGroupMaxQuantity(group, options) !== 1;
  }

  getSelectedCountForGroup(groupId: string): number {
    return Array.from(this.selectedOptions().values())
      .filter(o => o.OptionGroupID === groupId)
      .reduce((sum, o) => sum + (o.Quantity || 1), 0);
  }

  canIncreaseOption(group: OptionGroup, option: ProductOption, options: ProductOption[]): boolean {
    const groupMax = this.getGroupMaxQuantity(group, options);
    const currentGroupCount = this.getSelectedCountForGroup(group.id);
    if (groupMax > 0 && currentGroupCount >= groupMax) {
      return false;
    }
    const optKey = this.getOptionKey(option);
    const currentOptQty = this.getOptionQuantity(optKey);
    if (option.MaxQuantity > 0 && currentOptQty >= option.MaxQuantity) {
      return false;
    }
    return true;
  }

  increaseOptionQuantity(group: OptionGroup, option: ProductOption, options: ProductOption[], $event?: Event) {
    $event?.stopPropagation();
    if (!this.isSellable()) return;
    if (!this.canIncreaseOption(group, option, options)) return;

    const current = new Map(this.selectedOptions());
    const optKey = this.getOptionKey(option);
    const optQty = this.getOptionQuantity(optKey);
    const optType = this.resolveType(option, group);
    current.set(optKey, { ...option, Type: optType, Quantity: optQty + 1 });
    this.selectedOptions.set(current);
  }

  decreaseOptionQuantity(group: OptionGroup, option: ProductOption, $event?: Event) {
    $event?.stopPropagation();
    if (!this.isSellable()) return;
    const current = new Map(this.selectedOptions());
    const optKey = this.getOptionKey(option);
    const optQty = this.getOptionQuantity(optKey);
    if (optQty > 1) {
      const optType = this.resolveType(option, group);
      current.set(optKey, { ...option, Type: optType, Quantity: optQty - 1 });
    } else if (optQty === 1) {
      current.delete(optKey);
    }
    this.selectedOptions.set(current);
  }

  toggleOption(group: OptionGroup, option: ProductOption, options: ProductOption[]) {
    if (!this.isSellable()) return;
    const current = new Map(this.selectedOptions());
    const optKey = this.getOptionKey(option);
    const optType = this.resolveType(option, group);

    // MULTIPLY & SIZE: Mandatory single selection (switches to clicked option)
    if (optType === OptionType.MULTIPLY || optType === OptionType.SIZE) {
      for (const [key, opt] of current.entries()) {
        if (opt.OptionGroupID === group.id) {
          current.delete(key);
        }
      }
      current.set(optKey, { ...option, Type: optType, Quantity: 1 });
      this.selectedOptions.set(current);
      return;
    }

    // REMOVABLE: Multi-select checkbox toggle
    if (optType === OptionType.REMOVABLE) {
      if (current.has(optKey)) {
        current.delete(optKey);
      } else {
        current.set(optKey, { ...option, Type: optType, Quantity: 1 });
      }
      this.selectedOptions.set(current);
      return;
    }

    // ADD / PRODUCT with stepper
    if (this.isStepperGroup(group, options)) {
      const optQty = this.getOptionQuantity(optKey);
      if (optQty === 0) {
        if (this.canIncreaseOption(group, option, options)) {
          current.set(optKey, { ...option, Type: optType, Quantity: 1 });
          this.selectedOptions.set(current);
        }
      }
      return;
    }

    // Single choice ADD / PRODUCT (max == 1)
    const optQty = this.getOptionQuantity(optKey);
    const min = this.getGroupMinQuantity(group, options);

    if (optQty > 0 && min === 0) {
      current.delete(optKey);
    } else {
      for (const [key, opt] of current.entries()) {
        if (opt.OptionGroupID === group.id) {
          current.delete(key);
        }
      }
      current.set(optKey, { ...option, Type: optType, Quantity: 1 });
    }

    this.selectedOptions.set(current);
  }

  getMultiplierLabel(option: ProductOption): string {
    const mult = typeof option.Modifier === 'number' ? option.Modifier : 1;
    const multText = `(x${formatMultiplier(mult)})`;
    const basePrice = this.productEffectiveBasePrice();
    const diff = basePrice * (mult - 1.0);
    const tax = this.productEffectiveTaxPercent();
    const displayDiff = diff * (1 + tax / 100);

    if (Math.abs(diff) >= 0.005) {
      const sign = diff > 0 ? '+' : '-';
      const formattedDiff = Math.abs(displayDiff).toLocaleString('en-US', {
        minimumFractionDigits: this.currencyPrecision,
        maximumFractionDigits: this.currencyPrecision,
      });
      return `${multText} (${sign}${this.companyCurrency} ${formattedDiff})`;
    }
    return multText;
  }

  increaseQuantity() {
    if (!this.isSellable()) return;
    this.quantity.update(q => q + 1);
  }

  decreaseQuantity() {
    if (!this.isSellable()) return;
    this.quantity.update(q => (q > 1 ? q - 1 : 1));
  }

  onAddToCart() {
    const p = this.product();
    if (!p || !this.isSelectionValid() || !this.isSellable()) return;

    const optionsList = Array.from(this.selectedOptions().values());
    this.cartService.addToCart(p, optionsList, this.quantity(), this.notes().trim());

    this.close();
  }

  getDisplayText(value: any): string {
    return TextUtils.getLocalizedText(value, this.activeMenuLang()?.Code);
  }

  close() {
    this.modalService.close();
  }
}
